import { ArrowDown, ArrowUp, SlidersHorizontal } from "@phosphor-icons/react";
import { DEFAULT_SORT_DIR, parseQuery } from "@plannello/core/query/parse";
import { m } from "@plannello/i18n/messages";
import type { ReactNode } from "react";
import { Dialog, DialogTrigger, Heading, type Key, ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { type GroupBy, groupingOptions, groupParam, parseGroupBy } from "@/components/items/grouping";
import { SORT_KEYS, useSetViewSearch, type ViewSearch } from "@/components/items/viewSearch";
import {
  Button,
  IconButton,
  Popover,
  Select,
  Switch,
  segmentItem,
  segmentItemSelected,
  segmentTrack,
} from "@/components/ui";
import { useCatalog } from "@/data/CatalogProvider";
import { cx } from "@/lib/cx";
import {
  COLUMN_KEYS,
  type ColumnKey,
  DEFAULT_COLUMNS,
  DENSITIES,
  hasCustomState,
  type ItemLayout,
  parseColumns,
  parseDensity,
  parseTimelineRows,
  parseZoom,
  TIMELINE_ROWS,
  TIMELINE_ZOOMS,
} from "./viewState";

export const sortLabel = (k: (typeof SORT_KEYS)[number]) =>
  ({
    rank: m.sort_rank(),
    priority: m.sort_priority(),
    updated: m.sort_updated(),
    created: m.sort_created(),
    due: m.sort_due(),
    title: m.sort_title(),
    number: m.sort_number(),
  })[k];

export const columnLabel = (k: ColumnKey) =>
  ({
    priority: m.prop_priority(),
    key: m.disp_col_id(),
    type: m.prop_type(),
    status: m.prop_status(),
    fields: m.disp_col_fields(),
    milestone: m.prop_milestone(),
    start: m.prop_start(),
    due: m.prop_due(),
    estimate: m.prop_estimate(),
    created: m.prop_created(),
    updated: m.prop_updated(),
    assignee: m.prop_assignees(),
  })[k];

const densityLabel = (d: (typeof DENSITIES)[number]) =>
  ({
    compact: m.disp_density_compact(),
    comfortable: m.disp_density_comfortable(),
    spacious: m.disp_density_spacious(),
  })[d];

export const rowsLabel = (r: (typeof TIMELINE_ROWS)[number]) =>
  ({ epics: m.tl_rows_epics(), all: m.tl_rows_all(), milestone: m.tl_rows_milestone() })[r];

export const zoomLabel = (z: (typeof TIMELINE_ZOOMS)[number]) =>
  ({ weeks: m.tl_zoom_weeks(), months: m.tl_zoom_months(), quarters: m.tl_zoom_quarters() })[z];

/** Splits `?sort=` into key and direction ("priority-asc" -> priority / asc). */
function parseSort(raw: string) {
  const s = parseQuery(`sort:${raw}`).sort[0];
  if (!s || s.key === "group") return { key: s?.group ?? raw, dir: s?.dir ?? "asc", isDefault: true };
  return { key: s.key as string, dir: s.dir, isDefault: s.dir === DEFAULT_SORT_DIR[s.key] };
}

/**
 * Display options for item views, in one popover. List: grouping, sub-grouping, ordering with
 * direction, sub-items as a tree, density and visible properties. Board: columns (built-ins and
 * single-select Fields only) and ordering. Timeline: rows, zoom and sub-items. Every choice is URL
 * state, so the views menu can save it and the dirty indicator can compare it.
 */
export function DisplayOptions({
  layout,
  search,
  defaultGroup = "status",
  defaultSort = "rank",
  allowProduct,
}: {
  layout: ItemLayout;
  search: ViewSearch;
  defaultGroup?: GroupBy;
  /** `?sort=` value the view uses when the URL names none. */
  defaultSort?: string;
  allowProduct?: boolean;
}) {
  const catalog = useCatalog();
  const setSearch = useSetViewSearch();
  const groupBy = parseGroupBy(search.group, catalog, defaultGroup);
  const sort = parseSort(search.sort ?? defaultSort);
  const custom = hasCustomState(layout, {
    ...search,
    q: undefined,
    group: search.group === groupParam(defaultGroup) ? undefined : search.group,
    sort: search.sort === defaultSort ? undefined : search.sort,
  });

  const groups = groupingOptions(catalog, { product: allowProduct && layout === "list" });
  const groupItems = [
    ...groups.map((g) => ({ id: groupParam(g.id), label: g.label })),
    ...(layout === "list" ? [{ id: "none", label: m.group_none() }] : []),
  ];
  const boardGroup = groupBy === "none" || groupBy === "product" ? "status" : groupBy;
  const subItems = [
    { id: "none", label: m.group_none() },
    ...groups
      .filter((g) => g.id !== groupBy && g.id !== "product")
      .map((g) => ({ id: groupParam(g.id), label: g.label })),
  ];
  const sub = parseGroupBy(search.sub, catalog, "none");
  const columns = parseColumns(search.cols);
  const density = parseDensity(search.density);
  const sortKey = (SORT_KEYS as readonly string[]).includes(sort.key) ? sort.key : "rank";

  const setSort = (key: string, flip = false) => {
    const k = key as keyof typeof DEFAULT_SORT_DIR;
    const def = DEFAULT_SORT_DIR[k] ?? "asc";
    const dir = flip ? (sort.dir === "asc" ? "desc" : "asc") : def;
    const next = key === "rank" ? "rank" : dir === def ? key : `${key}-${dir}`;
    setSearch({ sort: next === defaultSort ? undefined : next });
  };

  const setColumns = (keys: Set<Key>) => {
    const next = COLUMN_KEYS.filter((k) => keys.has(k));
    const same = next.length === DEFAULT_COLUMNS.length && next.every((k, i) => k === DEFAULT_COLUMNS[i]);
    setSearch({ cols: same ? undefined : next.length ? next.join(",") : "none" });
  };

  const reset = () =>
    setSearch({
      group: undefined,
      sub: undefined,
      sort: undefined,
      cols: undefined,
      subs: undefined,
      density: undefined,
      rows: undefined,
      zoom: undefined,
    });

  return (
    <DialogTrigger>
      <Button variant="ghost" size="sm" aria-label={m.view_display()}>
        <SlidersHorizontal size={14} aria-hidden="true" />
        <span className="max-sm:hidden">{m.view_display()}</span>
        {custom ? <span className="sr-only">, {m.disp_customized()}</span> : null}
      </Button>
      <Popover placement="bottom end" className="w-80 max-w-[calc(100vw-24px)] p-0">
        <Dialog aria-label={m.view_display()} className="flex flex-col outline-none">
          <div className="flex flex-col gap-3 p-3">
            {layout === "timeline" ? (
              <>
                <Select
                  label={m.tl_rows()}
                  items={TIMELINE_ROWS.map((r) => ({ id: r, label: rowsLabel(r) }))}
                  selectedKey={parseTimelineRows(search.rows)}
                  onSelectionChange={(k) => setSearch({ rows: k === "epics" ? undefined : k })}
                />
                <Segmented
                  label={m.tl_zoom()}
                  value={parseZoom(search.zoom)}
                  options={TIMELINE_ZOOMS.map((z) => ({ id: z, label: zoomLabel(z) }))}
                  onChange={(z) => setSearch({ zoom: z === "months" ? undefined : z })}
                />
              </>
            ) : (
              <>
                <Select
                  label={layout === "board" ? m.disp_columns() : m.view_group_by()}
                  description={layout === "board" ? m.disp_board_group_hint() : undefined}
                  items={groupItems}
                  selectedKey={groupParam(layout === "board" ? boardGroup : groupBy)}
                  onSelectionChange={(k) =>
                    setSearch({
                      group: k === groupParam(defaultGroup) ? undefined : k,
                      ...(k === "none" ? { sub: undefined } : {}),
                    })
                  }
                />
                {layout === "list" ? (
                  <Select
                    label={m.disp_sub_group_by()}
                    items={subItems}
                    isDisabled={groupBy === "none"}
                    selectedKey={sub === groupBy ? "none" : groupParam(sub)}
                    onSelectionChange={(k) => setSearch({ sub: k === "none" ? undefined : k })}
                  />
                ) : null}
                <div className="flex items-end gap-1.5">
                  <Select
                    label={m.view_sort_by()}
                    className="min-w-0 flex-1"
                    items={SORT_KEYS.map((k) => ({ id: k, label: sortLabel(k) }))}
                    selectedKey={sortKey}
                    onSelectionChange={(k) => setSort(k)}
                  />
                  <IconButton
                    aria-label={sort.dir === "asc" ? m.disp_sort_ascending() : m.disp_sort_descending()}
                    icon={sort.dir === "asc" ? ArrowUp : ArrowDown}
                    variant="secondary"
                    size="lg"
                    isDisabled={sortKey === "rank"}
                    onPress={() => setSort(sortKey, true)}
                  />
                </div>
              </>
            )}
          </div>
          {layout !== "board" ? (
            <div className="flex flex-col gap-3 border-t border-line p-3">
              <Switch
                isSelected={search.subs !== "off"}
                onChange={(on) => setSearch({ subs: on ? undefined : "off" })}
                description={layout === "timeline" ? m.disp_sub_items_timeline_hint() : m.disp_sub_items_hint()}
              >
                {m.disp_sub_items()}
              </Switch>
              {layout === "list" ? (
                <Segmented
                  label={m.disp_density()}
                  value={density ?? "auto"}
                  options={[
                    { id: "auto", label: m.disp_density_auto() },
                    ...DENSITIES.map((d) => ({ id: d, label: densityLabel(d) })),
                  ]}
                  onChange={(d) => setSearch({ density: d === "auto" ? undefined : d })}
                />
              ) : null}
            </div>
          ) : null}
          {layout === "list" ? (
            <section aria-labelledby="disp-props" className="flex flex-col gap-2 border-t border-line p-3">
              <Heading id="disp-props" level={3} className="text-sm font-medium text-fg">
                {m.disp_properties()}
              </Heading>
              <ToggleButtonGroup
                aria-labelledby="disp-props"
                selectionMode="multiple"
                selectedKeys={new Set(columns)}
                onSelectionChange={setColumns}
                className="flex flex-wrap gap-1.5"
              >
                {COLUMN_KEYS.map((k) => (
                  <ToggleButton key={k} id={k} className={pill}>
                    {columnLabel(k)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </section>
          ) : null}
          <div className="flex items-center justify-end border-t border-line px-3 py-2">
            <Button variant="ghost" size="sm" isDisabled={!custom} onPress={reset}>
              {m.disp_reset()}
            </Button>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/** Toggle pill (properties): pressed = accent-soft fill, never colour only (aria-pressed). */
export const pill = cx(
  "press transition-ui inline-flex h-(--control-h-sm) items-center rounded-control border px-2 text-xs font-medium outline-hidden",
  "border-line text-fg-muted data-hovered:bg-hover data-hovered:text-fg",
  "data-selected:border-accent data-selected:bg-accent-soft data-selected:text-accent-soft-fg",
  "data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-offset-1 data-focus-visible:outline-focus",
);

/** Single-choice segmented control with a visible label (React Aria ToggleButtonGroup). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  hideLabel,
}: {
  label: string;
  value: T;
  options: Array<{ id: T; label: ReactNode; ariaLabel?: string }>;
  onChange: (v: T) => void;
  hideLabel?: boolean;
}) {
  return (
    <div className="flex flex-col gap-(--gap-field)">
      {hideLabel ? null : <span className="text-sm font-medium text-fg">{label}</span>}
      <ToggleButtonGroup
        aria-label={label}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={new Set([value])}
        onSelectionChange={(keys) => {
          const k = [...keys][0];
          if (k !== undefined) onChange(String(k) as T);
        }}
        className={segmentTrack}
      >
        {options.map((o) => (
          <ToggleButton
            key={o.id}
            id={o.id}
            aria-label={o.ariaLabel}
            className={cx(
              segmentItem,
              "flex-1 px-2 outline-hidden",
              segmentItemSelected,
              "data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
            )}
          >
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
}
